import { BillingStripeCreditCreateSchema } from "../../shared/billing-action-schemas";
import {
  BillingSignedDocumentsResponseSchema,
  BillingTermsFromDocumentSchema,
} from "../../shared/billing-document-schemas";
import {
  BillingCustomerEmailSchema,
  BillingStripeInvoiceCloseSchema,
  BillingStripeInvoicePaidOutsideSchema,
  BillingSubscriptionRenewalSchema,
  type BillingPaymentMethod,
} from "../../shared/billing-action-schemas";
import {
  BillingCreditGrantResponseSchema,
  BillingCustomerLinkSchema,
  BillingCustomerResponseSchema,
  BillingHistoryResponseSchema,
  BillingInvoicePaymentRecordSchema,
  BillingInvoiceResponseSchema,
  BillingInvoiceStatusChangeSchema,
  BillingManualInvoiceCreateSchema,
  BillingManualSubscriptionCreateSchema,
  BillingOverviewResponseSchema,
  BillingQuoteInputSchema,
  BillingQuoteResponseSchema,
  BillingQuoteSendSchema,
  BillingQuoteUpdateSchema,
  BillingReconcileResponseSchema,
  BillingRefundCreateSchema,
  BillingRefundResponseSchema,
  BillingStripeInvoiceCreateSchema,
  BillingStripeSubscriptionCreateSchema,
  BillingSubscriptionCancelSchema,
  BillingSubscriptionResponseSchema,
  BillingTermsInputSchema,
  BillingTermsResponseSchema,
  BillingTermsUpdateSchema,
  type BillingManualInvoiceCreate,
  type BillingManualSubscriptionCreate,
  type BillingQuoteInput,
  type BillingQuoteUpdate,
  type BillingRefundCreate,
  type BillingStripeInvoiceCreate,
  type BillingStripeSubscriptionCreate,
  type BillingSubscriptionCancel,
  type BillingTermsInput,
  type BillingTermsUpdate,
} from "../../shared/billing-schemas";
import { parseResponse } from "../api/client-core";
import { validated } from "./billing-validation";

/**
 * The billing surface of the operations console (ADR-0073). Every call parses its input through
 * the shared schema before sending, so the console can never submit a shape the server refuses;
 * a failed parse throws a `BillingFormError` keyed by schema field, which the form translates.
 */

const API_ROOT = "/api/ops";

export function billingQueryKey(customerSlug: string) {
  return ["ops", "customers", "billing", customerSlug] as const;
}

function billingPath(customerSlug: string, suffix = ""): string {
  return `${API_ROOT}/customers/${encodeURIComponent(customerSlug)}/billing${suffix}`;
}

/** The quote as a file. A plain link, because the browser is what saves it (ADR-0077). */
export function quotePdfHref(customerSlug: string, quoteId: string): string {
  return billingPath(customerSlug, `/quotes/${encodeURIComponent(quoteId)}/pdf`);
}

async function post<TSchema extends Parameters<typeof parseResponse>[1]>(
  path: string,
  body: unknown,
  schema: TSchema,
) {
  return parseResponse(
    await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    schema,
  );
}

export async function getBilling(customerSlug: string) {
  return parseResponse(
    await fetch(billingPath(customerSlug), { credentials: "same-origin" }),
    BillingOverviewResponseSchema,
  );
}

/** Under the billing key, so refreshing billing after a change refreshes these too. */
export function signedDocumentsKey(customerSlug: string) {
  return [...billingQueryKey(customerSlug), "signed-documents"] as const;
}

/** The customer's signed order forms and change orders, and which are set up for billing. */
export async function getSignedDocuments(customerSlug: string) {
  return parseResponse(
    await fetch(billingPath(customerSlug, "/signed-documents"), { credentials: "same-origin" }),
    BillingSignedDocumentsResponseSchema,
  );
}

/** Add a signed order form to billing as it reads. Refused with a reason when it needs review. */
export async function addSignedDocumentToBilling(customerSlug: string, documentId: string) {
  return post(
    billingPath(customerSlug, `/signed-documents/${encodeURIComponent(documentId)}/add`),
    {},
    BillingTermsResponseSchema,
  );
}

/** The signed copy as filed in the customer's documents, opened in a new tab. */
export function signedCopyHref(customerSlug: string, customerDocumentId: string): string {
  return `/api/ops/customers/${encodeURIComponent(customerSlug)}/document-preview/${encodeURIComponent(customerDocumentId)}/file`;
}

/** Approved terms set up from one signed document, as reviewed in the terms form. */
export async function createTermsFromDocument(
  customerSlug: string,
  input: { documentId: string; terms: BillingTermsInput },
) {
  return post(
    billingPath(customerSlug, "/terms/from-document"),
    validated(BillingTermsFromDocumentSchema, input),
    BillingTermsResponseSchema,
  );
}

/** One page of billing history, newest first; pass the previous page's `nextCursor` for more. */
export async function getBillingHistory(
  customerSlug: string,
  options: { subject?: string; cursor?: string | null } = {},
) {
  const params = new URLSearchParams();
  if (options.subject) params.set("subject", options.subject);
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.size ? `?${params.toString()}` : "";
  return parseResponse(
    await fetch(billingPath(customerSlug, `/history${query}`), { credentials: "same-origin" }),
    BillingHistoryResponseSchema,
  );
}

export async function createBillingTerms(customerSlug: string, input: BillingTermsInput) {
  return post(
    billingPath(customerSlug, "/terms"),
    validated(BillingTermsInputSchema, input),
    BillingTermsResponseSchema,
  );
}

/** Terms active the moment they are created, the employee as approver; nothing is sent (ADR-0077). */
export async function createApprovedBillingTerms(customerSlug: string, input: BillingTermsInput) {
  return post(
    billingPath(customerSlug, "/terms/approved"),
    validated(BillingTermsInputSchema, input),
    BillingTermsResponseSchema,
  );
}

export async function updateBillingTerms(
  customerSlug: string,
  termsId: string,
  input: BillingTermsUpdate,
) {
  return parseResponse(
    await fetch(billingPath(customerSlug, `/terms/${encodeURIComponent(termsId)}`), {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(validated(BillingTermsUpdateSchema, input)),
    }),
    BillingTermsResponseSchema,
  );
}

export type TermsTransition = "approve" | "end" | "cancel";

export async function transitionBillingTerms(
  customerSlug: string,
  termsId: string,
  action: TermsTransition,
  input: { reason: string; endsAt?: number | null },
) {
  return post(
    billingPath(customerSlug, `/terms/${encodeURIComponent(termsId)}/${action}`),
    action === "end"
      ? { reason: input.reason, endsAt: input.endsAt ?? null }
      : { reason: input.reason },
    BillingTermsResponseSchema,
  );
}

export async function createBillingQuote(customerSlug: string, input: BillingQuoteInput) {
  return post(
    billingPath(customerSlug, "/quotes"),
    validated(BillingQuoteInputSchema, input),
    BillingQuoteResponseSchema,
  );
}

export async function updateBillingQuote(
  customerSlug: string,
  quoteId: string,
  input: BillingQuoteUpdate,
) {
  return parseResponse(
    await fetch(billingPath(customerSlug, `/quotes/${encodeURIComponent(quoteId)}`), {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(validated(BillingQuoteUpdateSchema, input)),
    }),
    BillingQuoteResponseSchema,
  );
}

/** Send a draft to the customer, or withdraw a draft or sent quote. */
export async function transitionBillingQuote(
  customerSlug: string,
  quoteId: string,
  action: "send" | "cancel",
  input: { reason: string; validUntil?: number | null },
) {
  return post(
    billingPath(customerSlug, `/quotes/${encodeURIComponent(quoteId)}/${action}`),
    action === "send"
      ? validated(BillingQuoteSendSchema, { validUntil: input.validUntil, reason: input.reason })
      : { reason: input.reason },
    BillingQuoteResponseSchema,
  );
}

export async function linkStripeCustomer(
  customerSlug: string,
  input: { billingEmail: string | null; reason: string },
) {
  return post(
    billingPath(customerSlug, "/stripe/customer"),
    validated(BillingCustomerLinkSchema, input),
    BillingCustomerResponseSchema,
  );
}

export async function changeStripeBillingEmail(
  customerSlug: string,
  input: { billingEmail: string; reason: string },
) {
  return post(
    billingPath(customerSlug, "/stripe/customer/email"),
    validated(BillingCustomerEmailSchema, input),
    BillingCustomerResponseSchema,
  );
}

export async function collectStripeInvoice(
  customerSlug: string,
  input: BillingStripeInvoiceCreate,
) {
  return post(
    billingPath(customerSlug, "/stripe/invoices"),
    validated(BillingStripeInvoiceCreateSchema, input),
    BillingInvoiceResponseSchema,
  );
}

export async function createStripeSubscription(
  customerSlug: string,
  input: BillingStripeSubscriptionCreate,
) {
  return post(
    billingPath(customerSlug, "/stripe/subscriptions"),
    validated(BillingStripeSubscriptionCreateSchema, input),
    BillingSubscriptionResponseSchema,
  );
}

export async function grantStripeCredit(
  customerSlug: string,
  input: { termsId: string; termsLineId: string; reason: string },
) {
  return post(
    billingPath(customerSlug, "/stripe/credits"),
    validated(BillingStripeCreditCreateSchema, input),
    BillingCreditGrantResponseSchema,
  );
}

export async function recordManualCredit(
  customerSlug: string,
  input: { termsId: string; termsLineId: string; reason: string },
) {
  return post(
    billingPath(customerSlug, "/credits"),
    validated(BillingStripeCreditCreateSchema, input),
    BillingCreditGrantResponseSchema,
  );
}

export async function retryStripeInvoice(customerSlug: string, invoiceId: string, reason: string) {
  return post(
    billingPath(customerSlug, `/stripe/invoices/${encodeURIComponent(invoiceId)}/retry`),
    { reason },
    BillingInvoiceResponseSchema,
  );
}

export async function retryStripeSubscription(
  customerSlug: string,
  subscriptionId: string,
  reason: string,
) {
  return post(
    billingPath(customerSlug, `/stripe/subscriptions/${encodeURIComponent(subscriptionId)}/retry`),
    { reason },
    BillingSubscriptionResponseSchema,
  );
}

export async function reconcileStripe(customerSlug: string) {
  return post(billingPath(customerSlug, "/stripe/reconcile"), {}, BillingReconcileResponseSchema);
}

export async function recordManualInvoice(customerSlug: string, input: BillingManualInvoiceCreate) {
  return post(
    billingPath(customerSlug, "/invoices"),
    validated(BillingManualInvoiceCreateSchema, input),
    BillingInvoiceResponseSchema,
  );
}

export async function recordInvoicePayment(
  customerSlug: string,
  invoiceId: string,
  input: { paidAt: number; method: BillingPaymentMethod | null; reason: string },
) {
  return post(
    billingPath(customerSlug, `/invoices/${encodeURIComponent(invoiceId)}/payment`),
    validated(BillingInvoicePaymentRecordSchema, input),
    BillingInvoiceResponseSchema,
  );
}

/** A Stripe invoice the customer paid some other way, such as a check. */
export async function recordPaidOutsideStripe(
  customerSlug: string,
  invoiceId: string,
  input: { paidAt: number; method: BillingPaymentMethod; reason: string },
) {
  return post(
    billingPath(customerSlug, `/stripe/invoices/${encodeURIComponent(invoiceId)}/paid-outside`),
    validated(BillingStripeInvoicePaidOutsideSchema, input),
    BillingInvoiceResponseSchema,
  );
}

/** Void a Stripe invoice sent in error, or write off one that will never be paid. */
export async function closeStripeInvoice(
  customerSlug: string,
  invoiceId: string,
  input: { outcome: "void" | "uncollectible"; reason: string },
) {
  return post(
    billingPath(customerSlug, `/stripe/invoices/${encodeURIComponent(invoiceId)}/close`),
    validated(BillingStripeInvoiceCloseSchema, input),
    BillingInvoiceResponseSchema,
  );
}

/** Move the day a Stripe subscription next invoices, charging and crediting nothing. */
export async function moveNextInvoice(
  customerSlug: string,
  subscriptionId: string,
  input: { nextInvoiceAt: number; reason: string },
) {
  return post(
    billingPath(
      customerSlug,
      `/stripe/subscriptions/${encodeURIComponent(subscriptionId)}/next-invoice`,
    ),
    validated(BillingSubscriptionRenewalSchema, input),
    BillingSubscriptionResponseSchema,
  );
}

export async function changeInvoiceStatus(
  customerSlug: string,
  invoiceId: string,
  input: { status: "past_due" | "void" | "uncollectible"; reason: string },
) {
  return post(
    billingPath(customerSlug, `/invoices/${encodeURIComponent(invoiceId)}/status`),
    validated(BillingInvoiceStatusChangeSchema, input),
    BillingInvoiceResponseSchema,
  );
}

export async function recordManualSubscription(
  customerSlug: string,
  input: BillingManualSubscriptionCreate,
) {
  return post(
    billingPath(customerSlug, "/subscriptions"),
    validated(BillingManualSubscriptionCreateSchema, input),
    BillingSubscriptionResponseSchema,
  );
}

export async function cancelBillingSubscription(
  customerSlug: string,
  subscriptionId: string,
  input: BillingSubscriptionCancel,
) {
  return post(
    billingPath(customerSlug, `/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`),
    validated(BillingSubscriptionCancelSchema, input),
    BillingSubscriptionResponseSchema,
  );
}

export async function refundBillingInvoice(
  customerSlug: string,
  invoiceId: string,
  input: BillingRefundCreate,
) {
  return post(
    billingPath(customerSlug, `/invoices/${encodeURIComponent(invoiceId)}/refunds`),
    validated(BillingRefundCreateSchema, input),
    BillingRefundResponseSchema,
  );
}
